import { create } from "zustand";
import type { GpsBounds, HeatmapCell, UserInMap } from "../api/personal";

interface MapState {
  users: UserInMap[];
  mode: "markers" | "heatmap";
  heatmapCells: HeatmapCell[];
  heatmapMax: number;
  lastCenter: [number, number] | null;
  lastZoom: number | null;
  searchTerm: string;
  /** Event Locations mode (R1): on/off for this session only, never persisted. */
  eventsMode: boolean;
  /** Apopilo places mode: on/off for this session only; exclusive with Event Locations mode. */
  apopiloMode: boolean;
  setUsersInBounds: (bounds: GpsBounds, users: UserInMap[]) => void;
  setHeatmap: (cells: HeatmapCell[], maxWeight: number) => void;
  upsertUser: (user: UserInMap) => void;
  removeUser: (userId: string) => void;
  setLastView: (center: [number, number], zoom: number) => void;
  setSearchTerm: (term: string) => void;
  setEventsMode: (on: boolean) => void;
  setApopiloMode: (on: boolean) => void;
}

function isWithin(bounds: GpsBounds, user: UserInMap): boolean {
  return (
    user.lat >= bounds.lat1 &&
    user.lat <= bounds.lat2 &&
    user.lng >= bounds.lng1 &&
    user.lng <= bounds.lng2
  );
}

export const useMapStore = create<MapState>((set) => ({
  users: [],
  mode: "markers",
  heatmapCells: [],
  heatmapMax: 0,
  lastCenter: null,
  lastZoom: null,
  searchTerm: "",
  eventsMode: false,
  apopiloMode: false,
  // Merge a bounds-query response: fetched users are upserted, users inside
  // the queried bounds but absent from the response are dropped (they left or
  // moved away), and users outside the bounds are kept so panning back to a
  // recently visited area shows cached markers instantly instead of a blank
  // map while the next fetch is in flight.
  setUsersInBounds: (bounds, fetched) =>
    set((s) => {
      const byUserId = new Map(fetched.map((u) => [u.userId, u]));
      const kept = s.users.filter(
        (u) => !byUserId.has(u.userId) && !isWithin(bounds, u),
      );
      return {
        users: [...kept, ...byUserId.values()],
        mode: "markers" as const,
      };
    }),
  setHeatmap: (cells, maxWeight) =>
    set({ mode: "heatmap", heatmapCells: cells, heatmapMax: maxWeight }),
  upsertUser: (user) =>
    set((s) => {
      const idx = s.users.findIndex((u) => u.userId === user.userId);
      if (idx >= 0) {
        const next = [...s.users];
        next[idx] = user;
        return { users: next };
      }
      return { users: [...s.users, user] };
    }),
  removeUser: (userId) =>
    set((s) => ({ users: s.users.filter((u) => u.userId !== userId) })),
  setLastView: (center, zoom) => set({ lastCenter: center, lastZoom: zoom }),
  setSearchTerm: (term) => set({ searchTerm: term }),
  // The two overlay modes never run together: turning one on turns the other off.
  setEventsMode: (on) => set(on ? { eventsMode: true, apopiloMode: false } : { eventsMode: false }),
  setApopiloMode: (on) => set(on ? { apopiloMode: true, eventsMode: false } : { apopiloMode: false }),
}));
